/**
 * YH No. (patient PIN) search, shared by every study list (PACS, Worklist, Orders, Dispatch/Consultant, Front Desk, QC Workstation).
 *
 * A YH No. is an exact patient identifier, so the moment one is entered the other filters
 * (status, modality, name, date range, etc.) are cleared once and the list shows just that
 * patient's studies. After that the filters are editable again: whatever the user sets next
 * is kept and sent along with the YH No. to narrow that patient's studies further.
 *
 * Lists name the field differently (`po_pin` vs `pat_pin`), and most carry a centre scope
 * that is an access boundary rather than a search filter; the reset puts that back to the
 * user's default (`scope`) instead of dropping it.
 */

type Filters = Record<string, any>;

export interface YhSearchOptions {
  /** Filter key holding the YH No. Defaults to `po_pin`. */
  pinKey?: string;
  /** The list's default scope (e.g. `{ center: getDefaultCenterFilter() }`), applied with the YH No. */
  scope?: Filters;
}

/** The trimmed YH No. from a filter object, or '' when none is entered. */
export const getYhPin = (filters: Filters | null | undefined, pinKey = 'po_pin'): string => {
  const value = filters?.[pinKey];
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * Call on every filter change with the filters before and after it. When the change is
 * the YH No. going from empty to filled (`entered`), the returned filters are just the
 * YH No. plus `scope`, and the caller should also clear its date range and modality
 * checkboxes. For every other change, including edits made once a YH No. is already
 * entered, the new filters are returned untouched.
 */
export const applyYhEntry = (
  previous: Filters | null | undefined,
  next: Filters | null | undefined,
  { pinKey = 'po_pin', scope }: YhSearchOptions = {}
): { entered: boolean; filters: Filters } => {
  const pin = getYhPin(next, pinKey);
  const entered = !!pin && !getYhPin(previous, pinKey);
  if (!entered) {
    return { entered, filters: next || {} };
  }
  return { entered, filters: { ...scope, [pinKey]: pin } };
};
